import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, AlertTriangle, Crown, Check } from "lucide-react";
import { announceBooking } from "@/components/FirstVisitLocation";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { PLANS, money } from "@/lib/daytime";
import { PassStep } from "./daytime/book/PassStep";
import { DateStep } from "./daytime/book/DateStep";
import { ExplorerStep } from "./daytime/book/ExplorerStep";
import { ExtrasStep } from "./daytime/book/ExtrasStep";
import { PayStep, Booked } from "./daytime/book/PayStep";
import { PaymentDialog } from "@/components/PaymentDialog";

const STEPS = ["PASS", "DATE", "EXPLORER", "EXTRAS", "PAY"];

function Progress({ step, go }) {
  return (
    <div className="grid grid-cols-5 gap-1.5" data-testid="booking-progress">
      {STEPS.map((s, i) => (
        <button key={s} disabled={i > step} onClick={() => go(i)} className="text-center" data-testid={`progress-${i}`}>
          <div className={`h-2 rounded-full transition-colors ${i <= step ? "bg-turquoise" : "bg-white/15"}`} />
          <p className={`text-[9px] sm:text-xs font-black mt-1 tracking-wide whitespace-nowrap ${i === step ? "text-gold" : i < step ? "text-white" : "text-white/40"}`}>{i + 1}. {s}</p>
        </button>
      ))}
    </div>
  );
}

function Memberships({ prices, benefits, nav }) {
  return (
    <div className="space-y-4" data-testid="memberships-section">
      <p className="font-display font-black text-navy text-2xl">Visit Often? Save With a Rock Pass.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {PLANS(prices).filter((p) => p.key !== "day").map((p) => (
          <div key={p.key} className="bg-white rounded-3xl border-2 border-slate-100 p-5 flex flex-col" style={{ borderTopColor: p.color, borderTopWidth: 6 }} data-testid={`member-plan-${p.key}`}>
            <p className="font-display font-black text-navy">{p.title.toUpperCase()}</p>
            <p className="font-display font-black text-3xl" style={{ color: p.color }}>{money(p.price)} <span className="text-sm text-slate-400">{p.per || ""}</span></p>
            <ul className="text-sm text-slate-600 mt-2 space-y-0.5 flex-1">{p.lines.map((l) => <li key={l}>• {l}</li>)}</ul>
            <Button onClick={() => nav(`/dashboard/daytime?buy=${p.key}`)} className="mt-4 rounded-full bg-navy text-white font-black h-12" data-testid={`buy-${p.key}`}>
              {p.key === "pass5" ? "BUY 5-VISIT PASS" : p.key === "pass10" ? "BUY 10-VISIT PASS" : "BECOME A MEMBER"}</Button>
          </div>))}
      </div>
      <div className="bg-gold/20 rounded-3xl p-5" data-testid="member-benefits">
        <p className="font-display font-black text-navy flex items-center gap-2"><Crown className="w-5 h-5 text-coral" /> Adventure Rock Members Receive:</p>
        <ul className="mt-2 grid sm:grid-cols-2 gap-1 text-sm text-navy">{benefits.map((b) => <li key={b} className="flex gap-1.5"><Check className="w-4 h-4 text-turquoise shrink-0 mt-0.5" />{b}</li>)}</ul>
        <p className="text-[11px] text-navy/60 mt-2">Member discount does not apply to the standard school pick-up / home drop-off fee.</p>
      </div>
    </div>
  );
}

export default function DayPassBook() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [kids, setKids] = useState([]);
  const [days, setDays] = useState([]);
  const [xp, setXp] = useState(null);
  const [step, setStep] = useState(0);
  const [main, setMain] = useState("");
  const [sibs, setSibs] = useState([]);
  const [date, setDate] = useState("");
  const [arrival, setArrival] = useState("");
  const [picks, setPicks] = useState([]);
  const [parent, setParent] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [x, setX] = useState({ mode: "stay", sup_hours: 0, ride: null, escort: null, promo_code: "" });
  const [slotInfo, setSlotInfo] = useState(null);
  const [q, setQ] = useState(null);
  const [acks, setAcks] = useState({});
  const [waivers, setWaivers] = useState({});
  const [pay, setPay] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const loadKids = useCallback(() => api.get("/children").then((r) => setKids(r.data)), []);
  useEffect(() => {
    api.get("/daytime/me").then((r) => setMe(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/daytime/calendar").then((r) => setDays(r.data)).catch(() => {});
    api.get("/addons/public").then((r) => setXp(r.data)).catch(() => {});
    loadKids();
  }, [loadKids]);
  const types = useMemo(() => [main, ...sibs].filter(Boolean), [main, sibs]);
  useEffect(() => { setPicks((p) => types.map((_, i) => p[i] || null)); }, [types]);
  useEffect(() => { if (date) api.get("/daytime/slots", { params: { date, pass_types: types.join(",") } }).then((r) => setSlotInfo(r.data)).catch(() => {}); }, [date, types]);
  const needsTime = types.some((t) => t !== "allday") || x.mode !== "stay";
  const body = () => ({ date, arrival: arrival || null, explorers: picks.map((k, i) => ({ child_id: k.id, pass_type: types[i] })), mode: x.mode, sup_hours: x.sup_hours,
    ride: x.ride, escort: x.escort, promo_code: x.promo_code, payment_method: pay, acks });
  const go = (i) => { setStep(i); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const toPay = async () => {
    if (x.mode !== "stay" && !arrival) { toast.error("Choose an arrival time for supervision"); return go(1); }
    setQ(null); go(4);
    try { const { data } = await api.post("/daytime/daypass/quote", body()); setQ(data); } catch (e) { toast.error(apiErr(e)); go(3); }
  };
  const book = async () => {
    setBusy(true);
    try {
      for (const k of picks.filter((k) => !k.waiver?.signed)) {
        const w = waivers[k.id] || {};
        await api.post(`/children/${k.id}/waiver`, { participation_ack: true, emergency_ack: true, media_consent: w.media_consent || "neither", signature: w.signature });
      }
      const { data } = await api.post("/daytime/daypass/book", body());
      setDone(data); window.scrollTo({ top: 0 }); announceBooking();
      if (data.amount_due > 0) { setPayOpen(true); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!me) return <p className="text-slate-400">Loading…</p>;
  const s = me.settings;
  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="daypass-book-page">
      <div className="bg-navy text-white rounded-3xl p-5 sm:p-7 grain relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-turquoise/20" />
        <div className="absolute right-16 -bottom-12 w-28 h-28 rounded-full bg-coral/25" />
        <div className="relative flex items-center gap-3"><img src={LOGO} alt="Adventure Rock" className="h-14 w-14 object-contain" />
          <p className="text-gold text-xs font-black tracking-[0.3em]">PLAY • CREATE • EXPLORE</p></div>
        <h1 className="relative font-display font-black text-3xl sm:text-5xl mt-3">Book Your Adventure Rock Day Pass</h1>
        <p className="relative text-white/75 mt-2 text-sm sm:text-base">Choose the pass that works best for your Explorer and secure your space before arriving.</p>
        <p className="relative mt-3 inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm font-bold"><Sun className="w-4 h-4 text-gold" /> Open Daily: {s.hours}</p>
        <p className="relative mt-3 text-xs sm:text-sm bg-coral/20 border border-coral/40 rounded-2xl px-3 py-2 flex gap-2" data-testid="walkin-notice"><AlertTriangle className="w-4 h-4 text-coral shrink-0 mt-0.5" /> Advance booking is strongly recommended. Walk-in spaces are not guaranteed and families may be turned away if we reach capacity.</p>
        {!done && <div className="relative mt-5"><Progress step={step} go={go} /></div>}
      </div>
      {done ? <Booked c={done} onView={() => nav("/dashboard/daytime")} onPay={() => setPayOpen(true)} /> : <>
        {step === 0 && <PassStep prices={s.prices} settings={s} main={main} setMain={setMain} sibs={sibs} setSibs={setSibs} onNext={() => go(1)} />}
        {step === 1 && <DateStep days={days} date={date} setDate={setDate} arrival={arrival} setArrival={setArrival} types={types} needsTime={needsTime} hours={s.hours} onBack={() => go(0)} onNext={() => go(2)} />}
        {step === 2 && <ExplorerStep slots={types} children={kids} picks={picks} setPicks={setPicks} user={user} parent={parent} setParent={setParent} reload={loadKids} onBack={() => go(1)} onNext={() => go(3)} />}
        {step === 3 && <ExtrasStep x={x} setX={setX} xp={xp} avail={xp?.availability} slotInfo={slotInfo} onBack={() => go(2)} onNext={toPay} />}
        {step === 4 && <PayStep q={q} date={date} x={x} unsigned={picks.filter((k) => k && !k.waiver?.signed)} acks={acks} setAcks={setAcks} waivers={waivers} setWaivers={setWaivers}
          pay={pay} setPay={setPay} busy={busy} onEdit={() => go(0)} onBook={book} />}
      </>}
      {step === 0 && !done && <Memberships prices={s.prices} benefits={s.member_benefits || []} nav={nav} />}
      {done && payOpen && <PaymentDialog order={{ payment_order_id: done.payment_order_id, amount: done.amount_due, reference: done.reference }} open={payOpen}
        onDone={() => {}} onClose={() => setPayOpen(false)} />}
    </div>
  );
}
